import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Type } from 'lucide-react';
import { Button } from './ui/button';
import { PrefSection, type PrefVariant } from '@/components/PrefSection';
import { useTextSize, type TextSize } from '@/hooks/useTextSize';
import { cn } from '@/lib/utils';

/**
 * App-wide text size, in two presentations.
 *
 * Default (no `variant`) is the header control: an icon-only ghost button
 * that opens a small menu, mirroring LanguageSwitcher. Each option renders
 * an "A" at its actual size so the visual is also the affordance — the
 * pattern BBC, NYT, gov.uk and Apple's accessibility surfaces all use.
 *
 * `variant="section"` (or `"card"`) is the Profile presentation: a
 * `PrefSection` row of preview tiles, sitting alongside ThemeSwitcher and
 * FontSwitcher in the Display Preferences card.
 *
 * Both live here on purpose. This overhaul branch had split them into two
 * components with the same filename — a header one on main, a Profile one
 * here — which would have silently replaced the header control in both
 * PageLayout and PatientPortalLayout on merge. One component with a
 * variant is also how ThemeSwitcher and FontSwitcher already work.
 */
type Option = {
  value: TextSize;
  /** Label shown in the menu / under the tile. */
  label: string;
  /** Tailwind text size class used on the "A" preview. */
  preview: string;
};

const OPTIONS: Option[] = [
  { value: 'small', label: 'Small', preview: 'text-xs' },
  { value: 'normal', label: 'Default', preview: 'text-sm' },
  { value: 'large', label: 'Large', preview: 'text-base' },
  { value: 'x-large', label: 'Extra large', preview: 'text-lg' },
];

/** Profile presentation — preview tiles inside a PrefSection. */
const TextSizeTiles = ({ variant }: { variant: PrefVariant }) => {
  const { t } = useTranslation();
  const [size, setSize] = useTextSize();

  return (
    <PrefSection
      variant={variant}
      icon={Type}
      title={t('Text size')}
      description={t('Base text size across the app. Saved to this browser.')}
    >
      <div className="grid gap-2 sm:grid-cols-4">
        {OPTIONS.map((o) => {
          const active = size === o.value;
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => setSize(o.value)}
              aria-pressed={active}
              className={cn(
                'flex flex-col items-center gap-2 rounded-md border p-3 transition-colors',
                active ? 'border-primary bg-primary/5' : 'border-border bg-surface hover:bg-muted',
              )}
            >
              <span
                aria-hidden
                className={cn('flex h-14 items-center font-semibold leading-none', o.preview)}
              >
                A
              </span>
              <span className="flex items-center gap-1.5 text-sm font-medium">
                {t(o.label)}
                {active && <Check className="h-3.5 w-3.5 text-primary" />}
              </span>
            </button>
          );
        })}
      </div>
    </PrefSection>
  );
};

export const TextSizeSwitcher = ({ variant }: { variant?: PrefVariant }) => {
  const { t } = useTranslation();
  const [size, setSize] = useTextSize();
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapper.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Hooks above run unconditionally — the early return has to come after
  // them, not before, or the header and Profile renders would call a
  // different number of hooks.
  if (variant) return <TextSizeTiles variant={variant} />;

  const choose = (next: TextSize) => {
    setOpen(false);
    setSize(next);
  };

  return (
    <div ref={wrapper} className="relative">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('Text size')}
        title={t('Text size')}
      >
        <Type className="h-4 w-4" />
      </Button>
      {open && (
        <ul
          role="menu"
          className="absolute right-0 top-full z-50 mt-1 w-48 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
        >
          {OPTIONS.map((opt) => {
            const active = opt.value === size;
            return (
              <li key={opt.value} role="none">
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={active}
                  onClick={() => choose(opt.value)}
                  className={cn(
                    'flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-muted',
                    active && 'font-medium',
                  )}
                >
                  <span className="flex items-baseline gap-3">
                    <span className={cn('w-6 text-center font-semibold', opt.preview)}>A</span>
                    <span>{t(opt.label)}</span>
                  </span>
                  {active && <Check className="h-3.5 w-3.5 text-primary" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
